import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import {
  CommunicationFragmentFragment,
  CommunicationStatus,
} from "generated-graphql/graphql";
import { useCallback } from "react";

gql(`
  fragment CommunicationFragment on Communication {
    id
    tenantId
    tenant {
      id
      name
    }
    communicationFacilities {
      id
      facility {
        id
        name
        customerFacilityId
        state
      }
    }
    tierIIReports {
        id
        tenantId
        reportKind
        reportingYear
        facilityId
        createdAt
        encampStatus
        facility {
          id
          name
          customerFacilityId
          streetAddress1
          city
          state
          zip
        }
    }
    submissions {
      id
      title
      submissionType
      status
    }
    recipientName
    communicationType
    status

    emailRecipientAddresses
    emailSubject
    emailFrom
    emailReplyTo
    emailTemplateName
    emailBody

    mailRecipientAddressLine1
    mailRecipientAddressLine2
    mailRecipientCity
    mailRecipientState
    mailRecipientZip
    communicationDocuments {
      id
      sequence
      document {
        id
        title
        documentType
        storageLink
      }
    }
    proofDocument {
      id
      title
    }
    lepcId
    lepc {
      ...LepcPicker
    }
    fireDepartmentId
    fireDepartment {
      ...FireDepartmentPicker
    }
    policeDepartmentId
    policeDepartment {
      ...PoliceDepartmentPicker
    }
    leadAgencyId
    leadAgency {
      ...LeadAgencyPicker
    }
    lobMail {
      status
      lobId
      lobUrl
    }
    trackingId
    jobId
    job {
      id
      status
      stepError
    }
    completedByUser {
      id
      email
    }
    completedAt
    createdAt
    createdBy
    updatedAt
    updatedBy
  }
`);

export const COMMUNICATIONS_QUERY = gql(`
  query Communications($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    communications(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...CommunicationFragment
      }
      count
    }
  }
`);

export const COMMUNICATION_QUERY = gql(`
  query Communication($id: ID!) {
    communication(id: $id) {
      ...CommunicationFragment
    }
  }
`);

export const UPDATE_COMMUNICATION_MUTATION = gql(`
  mutation UpdateCommunication($id: ID!, $input: UpdateCommunicationInput!, $draft: Boolean) {
    updateCommunication(id: $id, input: $input, draft: $draft) {
      ...CommunicationFragment
    }
  }
`);

export const CREATE_COMMUNICATIONS_MUTATION = gql(`
  mutation CreateCommunication($input: CreateCommunicationInput!, $draft: Boolean) {
    createCommunication(input: $input, draft: $draft) {
      ...CommunicationFragment
    }
  }
`);

export const COMMUNICATION_VALIDATOR_QUERY = gql(`
  query CommunicationValidator($input: CreateCommunicationInput!) {
    communicationValidator(input: $input) {
      ...issue
    }
  }
`);

export const EXEMPT_MAILING_ADDRESS_FROM_VERIFICATION_MUTATION = gql(`
  mutation ExemptMailingAddressFromVerification($input: ExemptMailingAddressFromVerificationInput!) {
    exemptMailingAddressFromVerification(input: $input)
  }
`);

export const useExemptMailingAddressFromVerification = () => {
  const alerts = useAlerts();
  const [mutate, { loading }] = useMutation(
    EXEMPT_MAILING_ADDRESS_FROM_VERIFICATION_MUTATION
  );
  const exemptMailingAddressFromVerification = useCallback(
    async (params: CommunicationFragmentFragment) => {
      if (
        !params.recipientName ||
        !params.mailRecipientAddressLine1 ||
        !params.mailRecipientCity ||
        !params.mailRecipientState ||
        !params.mailRecipientZip
      ) {
        return;
      }
      await mutate({
        variables: {
          input: {
            recipientName: params.recipientName,
            primaryLine: params.mailRecipientAddressLine1,
            secondaryLine: params.mailRecipientAddressLine2,
            city: params.mailRecipientCity,
            state: params.mailRecipientState,
            zipCode: params.mailRecipientZip,
          },
        },
      });
      alerts.success("Mailing address exempted from verification");
    },
    [mutate, alerts]
  );

  return {
    exemptMailingAddressFromVerification,
    loading,
  };
};

export const SYNC_PROOF_FROM_LOB_MUTATION = gql(`
  mutation SyncProofFromLob($communicationId: ID!) {
    syncProofFromLob(communicationId: $communicationId) {
      message
      communication {
        id
        ...CommunicationFragment
      }
    }
  }
`);

export const useSyncProofFromLob = () => {
  const alerts = useAlerts();
  const [mutate, { loading }] = useMutation(SYNC_PROOF_FROM_LOB_MUTATION);

  const syncProof = useCallback(
    async (communicationId: string) => {
      alerts.info("Syncing proof…");
      try {
        const result = await mutate({
          variables: { communicationId },
          refetchQueries: [
            { query: COMMUNICATIONS_QUERY },
            { query: COMMUNICATION_QUERY, variables: { id: communicationId } },
          ],
        });
        const message = result.data?.syncProofFromLob?.message;
        if (result.errors?.length) {
          alerts.error(result.errors.map((e) => e.message).join(", "));
        } else if (message) {
          alerts.success(message);
        }
      } catch (error) {
        const message =
          error instanceof Error && error.message
            ? error.message
            : "An unexpected error occurred while syncing the proof from Lob.";
        alerts.error(message);
      }
    },
    [alerts, mutate]
  );

  return { syncProof, loading };
};

export const REQUEUE_COMMUNICATION_MUTATION = gql(`
  mutation RequeueCommunication($id: ID!) {
    requeueCommunication(id: $id) {
      id
      status
      job {
        id
        status
        stepError
      }
    }
  }
`);

export const useRequeueCommunication = () => {
  const alerts = useAlerts();
  const [mutate, { loading }] = useMutation(REQUEUE_COMMUNICATION_MUTATION);

  const requeue = useCallback(
    async (params: CommunicationFragmentFragment) => {
      if (params.proofDocument) {
        alerts.error("Cannot requeue a communication with a proof document");
        return;
      }
      if (params.status === CommunicationStatus.Draft) {
        alerts.error("Cannot requeue a draft communication");
        return;
      }
      await mutate({ variables: { id: params.id } });
      alerts.success("Communication requeued");
    },
    [alerts, mutate]
  );

  return { requeue, loading };
};

export const GENERATE_COMBINED_PDF_FROM_DOCUMENTS_MUTATION = gql(`
  mutation GenerateCombinedPdfFromDocuments($input: GenerateCombinedPdfFromDocumentsInput!) {
    generateCombinedPdfFromDocuments(input: $input) {
      id
    }
  }
`);

export const useGenerateCombinedPdfFromDocuments = () => {
  const [mutate, { loading }] = useMutation(
    GENERATE_COMBINED_PDF_FROM_DOCUMENTS_MUTATION
  );

  const generateCombinedPdfFromDocuments = useCallback(
    async (documentIds: string[]) => {
      const result = await mutate({ variables: { input: { documentIds } } });
      return result.data?.generateCombinedPdfFromDocuments;
    },
    [mutate]
  );

  return { generateCombinedPdfFromDocuments, loading };
};
